import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import type { DelegationSettings } from '@/features/delegation/types/delegation.types';

interface DelegationNotificationSettingsProps {
  settings: DelegationSettings;
  onChange: (settings: DelegationSettings, isValid: boolean) => void;
}

export const DelegationNotificationSettings: React.FC<DelegationNotificationSettingsProps> = ({
  settings,
  onChange,
}) => {
  const {
    notifyDelegateOnAssignment,
    notifyDelegateOnStart,
    notifyManagerBeforeExpiry,
    expiryReminderHoursBefore,
    notifyManagerOnDelegateAction,
  } = settings;

  const isExpiryHoursValid =
    Number.isInteger(expiryReminderHoursBefore) &&
    expiryReminderHoursBefore >= 1 &&
    expiryReminderHoursBefore <= 168;

  const isValid = !notifyManagerBeforeExpiry || isExpiryHoursValid;

  const handleToggle = (field: keyof DelegationSettings, checked: boolean) => {
    const updated = { ...settings, [field]: checked };
    const valid = !updated.notifyManagerBeforeExpiry || isExpiryHoursValid;
    onChange(updated, valid);
  };

  const handleExpiryHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const parsed = parseInt(raw, 10);
    const updated = { ...settings, expiryReminderHoursBefore: isNaN(parsed) ? 0 : parsed };
    const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 168;
    onChange(updated, !updated.notifyManagerBeforeExpiry || valid);
  };

  const showExpiryHoursError = notifyManagerBeforeExpiry && !isExpiryHoursValid;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Delegation Notifications</CardTitle>

      </CardHeader>
      <CardContent className="space-y-6">

        {/* Delegate notifications subgroup */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-foreground">Delegate notifications</p>

          {/* Notify delegate on assignment */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notifyDelegateOnAssignment">Notify delegate when assigned</Label>
              <p className="text-sm text-muted-foreground">
                Send a platform feed notification to the delegate when a manager assigns them
              </p>
            </div>
            <Switch
              id="notifyDelegateOnAssignment"
              checked={notifyDelegateOnAssignment}
              onCheckedChange={(checked) => handleToggle('notifyDelegateOnAssignment', checked)}
            />
          </div>

          {/* Notify delegate on start */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notifyDelegateOnStart">Notify delegate when delegation starts</Label>
              <p className="text-sm text-muted-foreground">
                Send a platform feed notification to the delegate on the day their delegation begins
              </p>
            </div>
            <Switch
              id="notifyDelegateOnStart"
              checked={notifyDelegateOnStart}
              onCheckedChange={(checked) => handleToggle('notifyDelegateOnStart', checked)}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="border-t" />

        {/* Manager notifications subgroup */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-foreground">Manager notifications</p>

          {/* Notify manager before expiry */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notifyManagerBeforeExpiry">Notify manager before delegation expires</Label>
              <p className="text-sm text-muted-foreground">
                Send a platform feed notification to the manager before their delegation expires
              </p>
            </div>
            <Switch
              id="notifyManagerBeforeExpiry"
              checked={notifyManagerBeforeExpiry}
              onCheckedChange={(checked) => handleToggle('notifyManagerBeforeExpiry', checked)}
            />
          </div>

          {/* Expiry hours input — only shown when toggle is on */}
          {notifyManagerBeforeExpiry && (
            <div className="pl-4 border-l-2 border-muted space-y-2">
              <Label htmlFor="expiryReminderHoursBefore">
                Notify how many hours before expiry
              </Label>
              <div className="flex gap-2">
                <Input
                  id="expiryReminderHoursBefore"
                  type="number"
                  min={1}
                  max={168}
                  value={expiryReminderHoursBefore}
                  onChange={handleExpiryHoursChange}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const next = Math.min(168, expiryReminderHoursBefore + 1);
                      handleExpiryHoursChange({
                        target: { value: String(next) },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = Math.max(1, expiryReminderHoursBefore - 1);
                      handleExpiryHoursChange({
                        target: { value: String(next) },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }
                  }}
                  className={`h-10 w-20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none${showExpiryHoursError ? ' border-destructive focus-visible:ring-destructive' : ''}`}
                  aria-describedby={showExpiryHoursError ? 'expiry-hours-error' : 'expiry-hours-hint'}
                  aria-invalid={showExpiryHoursError}
                />
                <div className="flex items-center px-3 text-sm text-muted-foreground whitespace-nowrap">
                  hours
                </div>
              </div>

              {showExpiryHoursError ? (
                <p id="expiry-hours-error" className="text-xs text-destructive">
                  Please enter a value between 1 and 168.
                </p>
              ) : (
                <p id="expiry-hours-hint" className="text-xs text-muted-foreground">
                  How many hours before expiry the manager will be notified (max 168 hours / 7 days)
                </p>
              )}
            </div>
          )}

          {/* Notify manager on delegate action */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notifyManagerOnDelegateAction">Notify manager when delegate takes action</Label>
              <p className="text-sm text-muted-foreground">
                Send a platform feed notification to the manager when their delegate approves or returns a timesheet on their behalf
              </p>
            </div>
            <Switch
              id="notifyManagerOnDelegateAction"
              checked={notifyManagerOnDelegateAction}
              onCheckedChange={(checked) => handleToggle('notifyManagerOnDelegateAction', checked)}
            />
          </div>
        </div>

        {/* Cross-link note */}
        <p className="text-xs text-muted-foreground border-t pt-4">
          These settings are shared with the Delegation Settings page.
        </p>

      </CardContent>
    </Card>
  );
};
